<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import { mapGetters } from 'vuex';
import ButtonNext from 'next/button/Button.vue';
import NumberField from '../dynamicField/NumberField.vue';
import SelectField from '../dynamicField/SelectField.vue';
import TextField from '../dynamicField/TextField.vue';
import VariableField from '../dynamicField/VariableField.vue';
import { getStepPath, findStep } from '../../helpers/automationUtils';

export default {
  components: {
    ButtonNext,
    NumberField,
    SelectField,
    TextField,
    VariableField,
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['stepChange'],
  setup() {
    const { toLocale } = useAutomationLocales();
    return { v$: useVuelidate(), toLocale };
  },
  data() {
    return {
      fallbackName: this.$t('WGPT_AUTOMATIONS.EDITOR.FOR_EACH.FALLBACK_NAME'),
      updatedName: this.step.name,
      isEditing: false,
      updatedFieldMapping: this.step.attributes?.field_mapping || [],
    };
  },
  computed: {
    ...mapGetters({
      automationSteps: 'wgptAutomationBuilder/getSteps',
    }),
    maxIterationsCap() {
      const path = getStepPath(this.automationSteps, this.step.id) || [];
      const parentIds = path.slice(0, -1);
      let cap = 500;
      for (let i = 0; i < parentIds.length; i += 1) {
        const parent = findStep(this.automationSteps, parentIds[i]);
        if (parent && parent.action === 'for_each') {
          const nextId = path[i + 1];
          const isAncestor =
            parent.steps && parent.steps.some(s => s.id === nextId);
          if (isAncestor) {
            cap = Math.floor(cap * 0.1);
          }
        }
      }
      return cap;
    },
    stepName() {
      return this.step.name || this.fallbackName;
    },
    attributes() {
      return this.step.attributes || {};
    },
    collectionVariable() {
      return this.attributes.collection || '';
    },
    fieldMapping() {
      return this.attributes.field_mapping || [];
    },
    fieldMappingTypeOptions() {
      const options = [
        {
          value: 'text',
          label: 'ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.TYPE.OPTIONS.TEXT',
        },
        {
          value: 'number',
          label:
            'ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.TYPE.OPTIONS.NUMBER',
        },
        {
          value: 'boolean',
          label:
            'ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.TYPE.OPTIONS.BOOLEAN',
        },
        {
          value: 'array',
          label:
            'ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.TYPE.OPTIONS.ARRAY',
        },
      ];
      return options.map(opt => ({
        ...opt,
        label: this.toLocale(opt.label, opt.value),
      }));
    },
    enabled() {
      return !this.disabled;
    },
  },
  watch: {
    step(step, stepBefore) {
      if (step.id !== stepBefore.id || step.name !== this.updatedName) {
        this.updatedName = step.name;
      }
      this.updatedFieldMapping = step.attributes?.field_mapping || [];
    },
  },
  validations() {
    return {
      updatedFieldMapping: {
        duplicate: value => {
          const inheritedVariableNames = this.variables
            .filter(v => !v.wrapper)
            .map(({ name }) => name);
          const mappingVariableNames = (value || [])
            .map(m => m.variable_name)
            .filter(Boolean);
          const allVariables = [
            ...inheritedVariableNames,
            ...mappingVariableNames,
          ];
          const duplicates = allVariables.filter(
            (item, i, items) => items.indexOf(item) !== i
          );
          return duplicates.length === 0;
        },
      },
    };
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    updateAttribute(key, value) {
      this.$emit('stepChange', this.step.id, {
        attributes: { ...this.attributes, [key]: value },
      });
    },
    onCollectionChange(value) {
      this.updateAttribute('collection', value);
    },
    editName() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.nameInput.focus();
      });
    },
    onNameChange() {
      this.$emit('stepChange', this.step.id, { name: this.updatedName });
      this.isEditing = false;
      this.$refs.nameInput.blur();
    },
    cancelNameEditing() {
      this.isEditing = false;
    },
    addFieldMapping() {
      const mapping = [
        ...this.fieldMapping,
        { variable_name: '', type: 'text', json_key: '' },
      ];
      this.updateFieldMappingAttribute(mapping);
    },
    removeFieldMapping(index) {
      const mapping = this.fieldMapping.filter((_, i) => i !== index);
      this.updateFieldMappingAttribute(mapping);
    },
    updateFieldMapping(index, key, value) {
      const mapping = this.fieldMapping.map((item, i) => {
        if (i !== index) return item;
        return { ...item, [key]: value };
      });
      this.updateFieldMappingAttribute(mapping);
    },
    updateFieldMappingAttribute(mapping) {
      this.updatedFieldMapping = mapping;
      this.v$.updatedFieldMapping.$touch();
      this.updateAttribute('field_mapping', mapping);
    },
  },
};
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <header class="group pe-10 flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <input
          v-show="isEditing"
          ref="nameInput"
          v-model.trim="updatedName"
          :placeholder="fallbackName"
          class="name-input ignore-drag !ps-1 !bg-n-alpha-black2 disabled:!cursor-text !text-slate-900 dark:!text-slate-100 !border-none !outline !outline-2 !outline-n-blue-border -outline-offset-2 !m-0 text-xl !font-semibold !h-6"
          dir="auto"
          type="text"
          @blur="onNameChange"
          @keyup.enter="onNameChange"
          @keyup.esc="cancelNameEditing"
        />
        <h5
          v-if="!isEditing"
          class="grow text-base font-semibold text-black-900 dark:text-slate-200 select-none inline-flex items-center gap-1.5 overflow-hidden"
        >
          <span class="truncate">{{ stepName }}</span>
          <ButtonNext
            color="slate"
            size="xs"
            variant="ghost"
            icon="i-lucide-pencil"
            class="invisible group-hover:visible ignore-drag"
            @click="editName"
          />
        </h5>
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        <span>
          {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.FOR_EACH.NAME') }}
        </span>
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.FOR_EACH.DESCRIPTION') }}
      </p>
    </header>

    <div class="border-t border-n-weak" />

    <div class="flex flex-col gap-4">
      <!-- Collection -->
      <VariableField
        :value="collectionVariable"
        :variables="variables"
        label="ACTIONS.FOR_EACH.FORM_FIELDS.COLLECTION.LABEL"
        description="ACTIONS.FOR_EACH.FORM_FIELDS.COLLECTION.DESCRIPTION"
        required
        :disabled="disabled"
        :dirty="dirty"
        @change="onCollectionChange"
      />

      <!-- Max iterations -->
      <NumberField
        :value="attributes.max_iterations"
        :default-value="maxIterationsCap"
        :min="1"
        :max="maxIterationsCap"
        label="ACTIONS.FOR_EACH.FORM_FIELDS.MAX_ITERATIONS.LABEL"
        description="ACTIONS.FOR_EACH.FORM_FIELDS.MAX_ITERATIONS.DESCRIPTION"
        :show-variables-selector="false"
        :disabled="disabled"
        :dirty="dirty"
        @change="v => updateAttribute('max_iterations', v)"
      />
    </div>

    <div class="border-t border-n-weak" />

    <!-- Field Mapping -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="text-sm font-medium text-n-slate-12">
          {{ toLocale('ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.LABEL') }}
        </label>
        <p class="text-xs text-n-slate-11 mb-0">
          {{
            toLocale('ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.DESCRIPTION')
          }}
        </p>
      </div>

      <div
        class="rounded-xl flex flex-col gap-3"
        :class="{
          'field-mapping-error': v$.updatedFieldMapping.$error,
        }"
      >
        <div
          v-for="(mapping, index) in fieldMapping"
          :key="index"
          class="flex items-start gap-1.5"
        >
          <TextField
            :value="mapping.variable_name"
            placeholder="ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.VARIABLE_NAME.PLACEHOLDER"
            :show-variables-selector="false"
            alphanumeric-plus-underscore-only
            required
            :disabled="disabled"
            :dirty="dirty"
            class="flex-1 min-w-0"
            @change="v => updateFieldMapping(index, 'variable_name', v)"
          />
          <SelectField
            :value="mapping.type"
            default-value="text"
            :options="fieldMappingTypeOptions"
            placeholder="ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.TYPE.PLACEHOLDER"
            required
            :disabled="disabled"
            :dirty="dirty"
            class="flex-1 min-w-0"
            @change="v => updateFieldMapping(index, 'type', v)"
          />
          <TextField
            :value="mapping.json_key"
            placeholder="ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.JSON_KEY.PLACEHOLDER"
            :show-variables-selector="false"
            required
            :disabled="disabled"
            :dirty="dirty"
            class="flex-1 min-w-0"
            @change="v => updateFieldMapping(index, 'json_key', v)"
          />
          <ButtonNext
            v-if="enabled"
            color="ruby"
            variant="ghost"
            size="xs"
            icon="i-lucide-x"
            class="flex-shrink-0 mt-1"
            @click="removeFieldMapping(index)"
          />
        </div>

        <p
          v-if="v$.updatedFieldMapping.$error"
          class="min-w-0 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
        >
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.OUTPUT_MAPPING.ERRORS.DUPLICATE'
            )
          }}
        </p>
      </div>

      <ButtonNext
        v-if="enabled"
        color="slate"
        variant="faded"
        icon="i-lucide-plus"
        @click="addFieldMapping"
      >
        {{ toLocale('ACTIONS.FOR_EACH.FORM_FIELDS.FIELD_MAPPING.ADD_LABEL') }}
      </ButtonNext>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.field-mapping-error {
  @apply p-2 my-2 bg-n-ruby-5 mb-4;
}
</style>
